<script setup>
// O botão de importar o histórico do ChatData, na linha das abas da lista de conversas. Quem o mostra só para o
// administrador da conta e o super admin é o ChatTypeTabs; o controller confere de novo.
//
// O arquivo é lido aqui só para listar as origens dele ("Whatsapp", "site", "iframe"...) e pedir a caixa de cada uma.
// Quem confere e grava é o servidor.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ChatdataImportsAPI from 'dashboard/api/chatdataImports';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const dialogRef = ref(null);
const fileInput = ref(null);
const arquivo = ref(null);
const origens = ref([]);
const caixaPorOrigem = ref({});
const erroDoArquivo = ref('');
const enviando = ref(false);

const opcoesDeCaixa = computed(() => [
  { value: '', label: t('CHAT_LIST.CHATDATA_IMPORT.NAO_IMPORTAR') },
  ...inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);

const algumaCaixa = computed(() =>
  Object.values(caixaPorOrigem.value).some(Boolean)
);

const limpar = () => {
  arquivo.value = null;
  origens.value = [];
  caixaPorOrigem.value = {};
  erroDoArquivo.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

const abrir = () => {
  limpar();
  dialogRef.value?.open();
};

const lerArquivo = async () => {
  const file = fileInput.value?.files[0];
  limpar();
  if (!file) return;

  try {
    const dados = JSON.parse(await file.text());
    if (!Array.isArray(dados?.conversations)) throw new Error();

    const contagem = {};
    dados.conversations.forEach(conversa => {
      const origem = conversa.source || 'desconhecida';
      contagem[origem] = (contagem[origem] || 0) + 1;
    });
    origens.value = Object.entries(contagem).map(([nome, total]) => ({
      nome,
      total,
    }));
    caixaPorOrigem.value = Object.fromEntries(
      origens.value.map(({ nome }) => [nome, ''])
    );
    arquivo.value = file;
  } catch {
    erroDoArquivo.value = t('CHAT_LIST.CHATDATA_IMPORT.ARQUIVO_INVALIDO');
  }
};

const importar = async () => {
  if (!arquivo.value || !algumaCaixa.value) return;

  enviando.value = true;
  try {
    const { data } = await ChatdataImportsAPI.importar(
      arquivo.value,
      caixaPorOrigem.value
    );
    useAlert(t('CHAT_LIST.CHATDATA_IMPORT.SUCESSO', data));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CHAT_LIST.CHATDATA_IMPORT.ERRO')
    );
  } finally {
    enviando.value = false;
  }
};
</script>

<template>
  <div class="flex items-center">
    <Button
      v-tooltip.top="t('CHAT_LIST.CHATDATA_IMPORT.BOTAO')"
      icon="i-lucide-history"
      color="slate"
      variant="ghost"
      size="xs"
      :aria-label="t('CHAT_LIST.CHATDATA_IMPORT.BOTAO')"
      @click="abrir"
    />
    <Dialog
      ref="dialogRef"
      :title="t('CHAT_LIST.CHATDATA_IMPORT.TITULO')"
      :description="t('CHAT_LIST.CHATDATA_IMPORT.DESCRICAO')"
      :confirm-button-label="t('CHAT_LIST.CHATDATA_IMPORT.IMPORTAR')"
      :is-loading="enviando"
      :disable-confirm-button="enviando || !arquivo || !algumaCaixa"
      @confirm="importar"
    >
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm truncate text-n-slate-12">
            {{ arquivo?.name || t('CHAT_LIST.CHATDATA_IMPORT.NENHUM_ARQUIVO') }}
          </span>
          <Button
            :label="t('CHAT_LIST.CHATDATA_IMPORT.ESCOLHER')"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="fileInput?.click()"
          />
        </div>
        <p v-if="erroDoArquivo" class="mb-0 text-sm text-n-ruby-11">
          {{ erroDoArquivo }}
        </p>
        <div v-if="origens.length" class="flex flex-col gap-2">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CHAT_LIST.CHATDATA_IMPORT.ORIGENS') }}
          </p>
          <div
            v-for="origem in origens"
            :key="origem.nome"
            class="flex items-center justify-between gap-3"
          >
            <span class="text-sm text-n-slate-12">
              {{
                t('CHAT_LIST.CHATDATA_IMPORT.ORIGEM', {
                  nome: origem.nome,
                  total: origem.total,
                })
              }}
            </span>
            <Select
              v-model="caixaPorOrigem[origem.nome]"
              :options="opcoesDeCaixa"
              :aria-label="origem.nome"
              size="sm"
              class="w-56"
            />
          </div>
        </div>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="application/json,.json"
        class="hidden"
        @change="lerArquivo"
      />
    </Dialog>
  </div>
</template>
